import { LSC_BILLING_OWNER_MANAGED_COPY } from './billingCopy';
import type {
  LaunchReadinessLevel,
  LaunchReadinessSummary,
  LeagueSetupWizardResult,
  SetupWizardReasonCode,
  SetupWizardStep,
  SetupWizardStepStatus,
} from './leagueSetupWizard';
import type {
  LscLeagueReadiness,
  LscLeagueReadinessItem,
  LscLeagueReadinessItemStatus,
  LscLeagueReadinessOverallStatus,
} from './types';

const EVENTSUB_PATTERN = /eventsub/gi;
const REVIEW_QUEUE_PATTERN = /review queue/gi;
const IDENTITY_ATTESTATION_PATTERN = /candidates? need identity attestation/gi;
const NEEDS_ATTENTION_CLEAR_PATTERN = /needs attention clear/gi;

export const OPERATIONAL_READINESS_KEYS = new Set(['needs_attention', 'review_queue']);

export interface SetupReadinessSummary {
  overallStatus: LscLeagueReadinessOverallStatus;
  headline: string;
  description: string;
  blockerCount: number;
  warningCount: number;
  completedCount: number;
  totalCount: number;
  isComplete: boolean;
  shouldShowChecklist: boolean;
  showCompactReady: boolean;
  operationalAttentionCount: number;
  operationalAttentionHref?: string;
  topBlockers: Array<{
    key: string;
    title: string;
    description: string;
    actionLabel: string;
    actionHref: string;
  }>;
  nextActionLabel?: string;
  nextActionHref?: string;
}

export interface ReadinessMapperOptions {
  isOwner?: boolean;
}

function findItem(readiness: LscLeagueReadiness, key: string): LscLeagueReadinessItem | undefined {
  return readiness.items?.find((item) => item.key === key);
}

function findItems(readiness: LscLeagueReadiness, keys: string[]): LscLeagueReadinessItem[] {
  return keys
    .map((key) => findItem(readiness, key))
    .filter((item): item is LscLeagueReadinessItem => Boolean(item));
}

function sanitizeCopy(text: string): string {
  return text.replace(EVENTSUB_PATTERN, 'Twitch live detection');
}

export function sanitizeCustomerReadinessCopy(text: string, itemKey?: string): string {
  let copy = sanitizeCopy(text);
  copy = copy.replace(REVIEW_QUEUE_PATTERN, 'Needs Attention');
  copy = copy.replace(IDENTITY_ATTESTATION_PATTERN, 'archive items need review before they can be archived');
  copy = copy.replace(NEEDS_ATTENTION_CLEAR_PATTERN, 'Needs Attention');
  if (itemKey && OPERATIONAL_READINESS_KEYS.has(itemKey)) {
    copy = copy.replace(/commissioner review/gi, 'review');
  }
  return copy;
}

export function isOperationalReadinessKey(key: string | undefined): boolean {
  return Boolean(key && OPERATIONAL_READINESS_KEYS.has(key));
}

export function computeSetupChecklistScore(readiness: LscLeagueReadiness): {
  completedCount: number;
  totalCount: number;
} {
  const setupItems = (readiness.items ?? []).filter((item) => !isOperationalReadinessKey(item.key));
  const totalCount = setupItems.length;
  const completedCount = setupItems.filter(
    (item) => item.status === 'complete' || item.status === 'not_applicable'
  ).length;
  return {
    completedCount: Math.min(completedCount, totalCount),
    totalCount,
  };
}

export function formatSetupChecklistProgress(completedCount: number, totalCount: number): string {
  const safeTotal = Math.max(totalCount, 0);
  const safeCompleted = Math.min(Math.max(completedCount, 0), safeTotal);
  return `${safeCompleted} of ${safeTotal} steps ready`;
}

export function isCoreSetupComplete(readiness: LscLeagueReadiness): boolean {
  const score = readiness.readiness_score ?? { blocking: 0 };
  if ((score.blocking ?? 0) > 0) {
    return false;
  }
  const setupBlockers = (readiness.blocking_items ?? []).filter(
    (item) => !isOperationalReadinessKey(item.key)
  );
  if (setupBlockers.length > 0) {
    return false;
  }
  if (readiness.is_complete || readiness.should_show_checklist === false) {
    return true;
  }
  const setupActionable = (readiness.actionable_items ?? []).filter(
    (item) => !isOperationalReadinessKey(item.key)
  );
  return setupActionable.length === 0;
}

function operationalAttentionItems(readiness: LscLeagueReadiness): LscLeagueReadinessItem[] {
  const needsAttention = (readiness.items ?? []).find(
    (item) => item.key === 'needs_attention' && item.status === 'warning'
  );
  if (needsAttention) {
    return [needsAttention];
  }

  const warningItems = readiness.warning_items ?? [];
  const fromWarnings = warningItems.filter(
    (item) => item.key === 'needs_attention' && item.status === 'warning'
  );
  if (fromWarnings.length > 0) {
    return fromWarnings;
  }

  return [];
}

function operationalAttentionSummary(readiness: LscLeagueReadiness): {
  count: number;
  href?: string;
  messages: string[];
} {
  const messages: string[] = [];
  let count = 0;
  let href: string | undefined;

  for (const item of operationalAttentionItems(readiness)) {
    if (!isOperationalReadinessKey(item.key) || item.status === 'complete') {
      continue;
    }
    const metrics = (item.metrics ?? {}) as Record<string, number | undefined>;
    const itemCount = Number(metrics.needs_attention_count ?? metrics.count) || 0;
    if (itemCount <= 0) {
      continue;
    }
    count += itemCount;
    href = href ?? item.action_href;
    messages.push(sanitizeCustomerReadinessCopy(item.description, item.key));
  }

  return { count, href, messages };
}

function coreCompleteDescription(readiness: LscLeagueReadiness): string {
  const operational = operationalAttentionSummary(readiness);
  if (operational.count <= 0) {
    return 'Core setup is complete.';
  }
  if (operational.count === 1) {
    return 'Core setup is complete. 1 item needs attention.';
  }
  return `Core setup is complete. ${operational.count} items need attention.`;
}

const STATUS_RANK: Record<LscLeagueReadinessItemStatus, number> = {
  blocked: 4,
  warning: 3,
  complete: 0,
  not_applicable: 1,
};

function pickPrimaryItem(items: LscLeagueReadinessItem[]): LscLeagueReadinessItem | undefined {
  if (items.length === 0) return undefined;
  return items.reduce((worst, item) =>
    (STATUS_RANK[item.status] ?? 0) > (STATUS_RANK[worst.status] ?? 0) ? item : worst
  );
}

function toWizardStatus(
  item: LscLeagueReadinessItem,
  options: ReadinessMapperOptions
): SetupWizardStepStatus {
  if (
    item.key === 'billing_entitlement' &&
    options.isOwner === false &&
    item.status === 'not_applicable'
  ) {
    return 'locked';
  }
  if (item.admin_only && item.status === 'blocked' && options.isOwner === false) {
    return 'locked';
  }
  if (item.status === 'complete' || item.status === 'not_applicable') {
    return 'complete';
  }
  if (item.status === 'warning') return 'warning';
  if (item.status === 'blocked') return 'needs_action';
  return 'needs_action';
}

function stepFromItem(
  item: LscLeagueReadinessItem,
  config: {
    id: string;
    title: string;
    options: ReadinessMapperOptions;
    reasonCode: SetupWizardReasonCode;
    ownerOnly?: boolean;
    blocking?: boolean;
    optionalWhenComplete?: boolean;
  }
): SetupWizardStep {
  const status = toWizardStatus(item, config.options);
  const resolvedStatus =
    config.optionalWhenComplete && (status === 'complete' || status === 'warning')
      ? status === 'warning'
        ? 'warning'
        : 'optional'
      : status;

  return {
    id: config.id,
    title: config.title,
    status: resolvedStatus,
    description: sanitizeCustomerReadinessCopy(item.description, item.key),
    primaryActionLabel: item.action_label,
    primaryActionHref: item.action_href,
    commissionerVisible: true,
    ownerOnly: config.ownerOnly ?? Boolean(item.admin_only),
    blocking:
      config.blocking ??
      (item.status === 'blocked' && Boolean(item.is_actionable || item.severity === 'blocking')),
    reasonCode: config.reasonCode,
  };
}

function aggregateStep(
  items: LscLeagueReadinessItem[],
  config: {
    id: string;
    title: string;
    options: ReadinessMapperOptions;
    reasonCode: SetupWizardReasonCode;
    fallbackDescription: string;
    fallbackActionLabel: string;
    fallbackActionHref: string;
    ownerOnly?: boolean;
    optionalWhenComplete?: boolean;
  }
): SetupWizardStep {
  const primary = pickPrimaryItem(items);
  if (!primary) {
    return {
      id: config.id,
      title: config.title,
      status: 'needs_action',
      description: config.fallbackDescription,
      primaryActionLabel: config.fallbackActionLabel,
      primaryActionHref: config.fallbackActionHref,
      commissionerVisible: true,
      ownerOnly: config.ownerOnly ?? false,
      blocking: true,
      reasonCode: config.reasonCode,
    };
  }

  const descriptions = items
    .filter((item) => item.status !== 'complete' && item.status !== 'not_applicable')
    .map((item) => sanitizeCopy(item.description))
    .filter(Boolean);

  const step = stepFromItem(primary, {
    id: config.id,
    title: config.title,
    options: config.options,
    reasonCode: config.reasonCode,
    ownerOnly: config.ownerOnly,
    optionalWhenComplete: config.optionalWhenComplete,
  });

  if (descriptions.length > 1) {
    step.description = descriptions.join(' ');
  }

  step.blocking = items.some(
    (item) => item.status === 'blocked' && (item.is_actionable || item.severity === 'blocking')
  );

  return step;
}

export function mapReadinessToWizardSteps(
  readiness: LscLeagueReadiness,
  options: ReadinessMapperOptions = {}
): SetupWizardStep[] {
  const billing = findItem(readiness, 'billing_entitlement');
  const leagueProfile = findItem(readiness, 'league_profile');
  const scheduleItems = findItems(readiness, [
    'schedule_source_connected',
    'current_week_synced',
    'active_games',
    'games_visible',
  ]);
  const memberItems = findItems(readiness, [
    'members_imported',
    'twitch_linked',
    'team_assignments',
    'unlinked_schedule_teams',
  ]);
  const twitchItem = findItem(readiness, 'eventsub_ready');
  const automationItems = findItems(readiness, [
    'live_detection_active',
    'live_automation_policy',
    'archive_automation_policy',
    'automation_coverage',
  ]);

  const settingsHref = billing?.action_href ?? leagueProfile?.action_href ?? '/lsc';
  const importHref = readiness.league_id
    ? `/lsc/leagues/${readiness.league_id}/import`
    : settingsHref;
  const membersHref = readiness.league_id
    ? `/lsc/leagues/${readiness.league_id}/members`
    : settingsHref;

  const subscriptionStep = billing
    ? stepFromItem(billing, {
        id: 'subscription',
        title: 'Subscription',
        options,
        reasonCode:
          billing.status === 'complete' || billing.status === 'not_applicable'
            ? 'subscription_active'
            : 'subscription_missing',
        ownerOnly: options.isOwner === false,
        blocking: billing.status === 'blocked' && Boolean(billing.is_actionable),
      })
    : {
        id: 'subscription',
        title: 'Subscription',
        status: 'warning' as SetupWizardStepStatus,
        description: 'Subscription status is unavailable.',
        primaryActionLabel: 'League settings',
        primaryActionHref: settingsHref,
        commissionerVisible: true,
        ownerOnly: false,
        blocking: false,
        reasonCode: 'subscription_missing' as SetupWizardReasonCode,
      };

  if (options.isOwner === false && billing?.status === 'not_applicable') {
    subscriptionStep.status = 'locked';
    subscriptionStep.description = LSC_BILLING_OWNER_MANAGED_COPY;
    subscriptionStep.ownerOnly = true;
    subscriptionStep.blocking = true;
    subscriptionStep.reasonCode = 'subscription_owner_managed';
  }

  const leagueBasicsStep = leagueProfile
    ? stepFromItem(leagueProfile, {
        id: 'league_basics',
        title: 'League basics',
        options,
        reasonCode: 'league_basics_ready',
        blocking: leagueProfile.status === 'blocked',
      })
    : aggregateStep([], {
        id: 'league_basics',
        title: 'League basics',
        options,
        reasonCode: 'league_basics_ready',
        fallbackDescription: 'League profile status is unavailable.',
        fallbackActionLabel: 'League settings',
        fallbackActionHref: settingsHref,
      });

  const scheduleStep = aggregateStep(scheduleItems, {
    id: 'schedule_source',
    title: 'Schedule',
    options,
    reasonCode: scheduleItems.some((item) => item.status === 'warning')
      ? 'schedule_stale'
      : scheduleItems.some((item) => item.status === 'blocked')
        ? 'schedule_missing'
        : 'schedule_connected',
    fallbackDescription: 'Connect a schedule source or import games.',
    fallbackActionLabel: 'Import schedule',
    fallbackActionHref: importHref,
  });

  const membersStep = aggregateStep(memberItems, {
    id: 'members_teams',
    title: 'Members and teams',
    options,
    reasonCode: memberItems.some((item) => item.status === 'blocked')
      ? 'members_insufficient'
      : 'members_ready',
    fallbackDescription: 'Invite members and confirm team assignments.',
    fallbackActionLabel: 'Manage members',
    fallbackActionHref: membersHref,
  });

  const twitchStep = twitchItem
    ? stepFromItem(
        {
          ...twitchItem,
          title: 'Twitch live detection',
        },
        {
          id: 'twitch_live',
          title: 'Twitch live detection',
          options,
          reasonCode:
            twitchItem.status === 'complete'
              ? 'twitch_ready'
              : twitchItem.status === 'warning'
                ? 'twitch_provisioning'
                : 'twitch_missing',
          blocking: twitchItem.status === 'blocked',
        }
      )
    : aggregateStep([], {
        id: 'twitch_live',
        title: 'Twitch live detection',
        options,
        reasonCode: 'twitch_missing',
        fallbackDescription: 'Connect Twitch for broadcasters so live games can be detected.',
        fallbackActionLabel: 'Manage members',
        fallbackActionHref: membersHref,
      });

  const automationStep = aggregateStep(automationItems, {
    id: 'automation_settings',
    title: 'Automation settings',
    options,
    reasonCode: automationItems.every(
      (item) => item.status === 'complete' || item.status === 'not_applicable'
    )
      ? 'automation_ready'
      : 'automation_review',
    fallbackDescription: 'Review live and archive automation settings when you are ready.',
    fallbackActionLabel: 'Automation settings',
    fallbackActionHref: settingsHref,
    optionalWhenComplete: true,
  });

  return [
    subscriptionStep,
    leagueBasicsStep,
    scheduleStep,
    membersStep,
    twitchStep,
    automationStep,
  ];
}

export function mapReadinessToLaunchSummary(readiness: LscLeagueReadiness): LaunchReadinessSummary {
  const score = readiness.readiness_score ?? {
    completed: 0,
    total: 0,
    blocking: 0,
    warnings: 0,
    actionable: 0,
  };
  const needsAttention = findItem(readiness, 'needs_attention');
  const reviewQueue = findItem(readiness, 'review_queue');
  const coreComplete = isCoreSetupComplete(readiness);
  const operational = operationalAttentionSummary(readiness);

  const launchWarnings: string[] = [];
  if (needsAttention && needsAttention.status !== 'complete') {
    const count = Number(
      (needsAttention.metrics as { needs_attention_count?: number; count?: number } | undefined)
        ?.needs_attention_count ??
        (needsAttention.metrics as { count?: number } | undefined)?.count
    );
    if (Number.isFinite(count) && count > 0) {
      launchWarnings.push(`Needs Attention has ${count} item${count === 1 ? '' : 's'} to review`);
    } else {
      launchWarnings.push('Needs Attention has items to review');
    }
  }
  if (reviewQueue && reviewQueue.status === 'warning') {
    launchWarnings.push(sanitizeCustomerReadinessCopy(reviewQueue.description, reviewQueue.key));
  }

  (readiness.warning_items ?? []).forEach((item) => {
    if (!isOperationalReadinessKey(item.key)) {
      launchWarnings.push(sanitizeCustomerReadinessCopy(item.title, item.key));
    }
  });

  const blockers = (readiness.blocking_items ?? [])
    .filter((item) => !isOperationalReadinessKey(item.key))
    .map((item) => sanitizeCustomerReadinessCopy(item.title, item.key));

  const blockerCount = score.blocking;
  const warningCount = score.warnings;
  const billingItem = findItem(readiness, 'billing_entitlement');
  const billingComplete =
    billingItem?.status === 'complete' || billingItem?.status === 'not_applicable';

  let level: LaunchReadinessLevel;
  let headline: string;
  let description: string;

  if (coreComplete || readiness.is_complete || readiness.overall_status === 'ready') {
    level = 'ready';
    headline = warningCount > 0 ? 'Ready with warnings' : 'Ready';
    description = coreCompleteDescription(readiness);
  } else if (readiness.overall_status === 'blocked' || blockerCount > 0) {
    level = 'needs_setup';
    headline = billingComplete ? 'Finish setup' : 'Needs setup';
    description = `${blockerCount || blockers.length} blocker${(blockerCount || blockers.length) === 1 ? '' : 's'} must be resolved before your league is ready.`;
  } else if (
    readiness.overall_status === 'preview_only' ||
    readiness.overall_status === 'warning' ||
    warningCount > 0
  ) {
    level = 'almost_ready';
    headline = billingComplete ? 'Finish setup' : 'Almost ready';
    description = `${warningCount || launchWarnings.length} setup item${(warningCount || launchWarnings.length) === 1 ? '' : 's'} still need attention.`;
  } else {
    level = 'needs_setup';
    headline = billingComplete ? 'Finish setup' : 'Needs setup';
    description = 'Complete the remaining setup steps to get your league ready.';
  }

  if (operational.messages.length > 0 && coreComplete) {
    description = `${description} ${operational.messages[0]}`.trim();
  }

  const next = readiness.next_best_action;
  const nextAction =
    next && !isOperationalReadinessKey(next.key)
      ? next
      : coreComplete && operational.href
        ? {
            action_label: 'Open Needs Attention',
            action_href: operational.href,
          }
        : next;

  return {
    level,
    headline,
    description,
    blockers: blockers.length > 0 ? blockers : launchWarnings.slice(0, 3),
    blockerCount,
    warningCount,
    nextActionLabel: nextAction?.action_label,
    nextActionHref: nextAction?.action_href,
  };
}

export function resolveLeagueSetupWizardFromReadiness(
  readiness: LscLeagueReadiness,
  options: ReadinessMapperOptions = {}
): LeagueSetupWizardResult {
  const steps = mapReadinessToWizardSteps(readiness, options);
  const completedCount = steps.filter((step) => step.status === 'complete' || step.status === 'optional').length;
  return {
    steps,
    launchReadiness: mapReadinessToLaunchSummary(readiness),
    completedCount,
  };
}

export function mapReadinessToSetupSummary(
  readiness: LscLeagueReadiness
): SetupReadinessSummary {
  const score = readiness.readiness_score ?? {
    completed: 0,
    total: 0,
    blocking: 0,
    warnings: 0,
    actionable: 0,
  };

  const coreComplete = isCoreSetupComplete(readiness);
  const operational = operationalAttentionSummary(readiness);

  const actionableBlockers = (readiness.blocking_items ?? [])
    .concat(readiness.actionable_items ?? [])
    .filter(
      (item, index, list) =>
        list.findIndex((candidate) => candidate.key === item.key) === index &&
        !isOperationalReadinessKey(item.key) &&
        (item.status === 'blocked' || item.is_actionable)
    )
    .slice(0, 3);

  const next = readiness.next_best_action;
  const nextIsOperational = next?.key ? isOperationalReadinessKey(next.key) : false;
  const setupScore = computeSetupChecklistScore(readiness);

  return {
    overallStatus: readiness.overall_status,
    headline: coreComplete ? 'League ready' : setupSummaryHeadline(readiness.overall_status),
    description: coreComplete
      ? coreCompleteDescription(readiness)
      : next && !nextIsOperational
        ? sanitizeCustomerReadinessCopy(next.description ?? '', next.key)
        : readiness.is_complete
          ? 'League setup is complete.'
          : 'Finish subscription, schedule, members, Twitch, and automation readiness.',
    blockerCount: score.blocking,
    warningCount: score.warnings,
    completedCount: setupScore.completedCount,
    totalCount: setupScore.totalCount,
    isComplete: readiness.is_complete || coreComplete,
    shouldShowChecklist: coreComplete ? false : readiness.should_show_checklist,
    showCompactReady: coreComplete,
    operationalAttentionCount: operational.count,
    operationalAttentionHref: operational.href,
    topBlockers: actionableBlockers.map((item) => ({
      key: item.key,
      title: sanitizeCustomerReadinessCopy(item.title, item.key),
      description: sanitizeCustomerReadinessCopy(item.description, item.key),
      actionLabel: item.action_label,
      actionHref: item.action_href,
    })),
    nextActionLabel: nextIsOperational ? undefined : next?.action_label,
    nextActionHref: nextIsOperational ? operational.href : next?.action_href,
  };
}

export function mapReadinessToCommandCenterChecklist(readiness: LscLeagueReadiness) {
  const items = (readiness.items ?? []).map((item) => ({
    id: item.key,
    label: sanitizeCustomerReadinessCopy(item.title, item.key),
    status: item.status,
    explanation: sanitizeCustomerReadinessCopy(item.description, item.key),
    actionLabel: item.action_label,
    actionHref: item.action_href,
    isActionable: Boolean(item.is_actionable),
  }));

  const setupComplete = isCoreSetupComplete(readiness);
  const showChecklist = !setupComplete && readiness.should_show_checklist;
  const setupScore = computeSetupChecklistScore(readiness);

  const visibleItems = showChecklist
    ? items.filter(
        (item) =>
          !isOperationalReadinessKey(item.id) &&
          (item.status === 'blocked' || item.status === 'warning' || item.isActionable)
      )
    : [];

  const next = readiness.next_best_action;
  const operational = operationalAttentionSummary(readiness);

  return {
    items: visibleItems,
    totalCount: setupScore.totalCount,
    nextAction:
      next && !isOperationalReadinessKey(next.key)
        ? {
            priority: 1,
            title: sanitizeCustomerReadinessCopy(next.title ?? 'Next step', next.key),
            detail: sanitizeCustomerReadinessCopy(next.description ?? '', next.key),
            actionLabel: next.action_label ?? 'Open',
            actionHref: next.action_href ?? '/lsc',
            severity: next.severity === 'blocked' ? ('blocked' as const) : ('warning' as const),
          }
        : operational.href && operational.count > 0
          ? {
              priority: 2,
              title: 'Needs Attention',
              detail: coreCompleteDescription(readiness),
              actionLabel: 'Open Needs Attention',
              actionHref: operational.href,
              severity: 'warning' as const,
            }
          : null,
    setupComplete,
    setupIncomplete: !setupComplete && readiness.should_show_checklist,
    completedCount: setupScore.completedCount,
    shouldShowChecklist: showChecklist,
    overallStatus: setupComplete ? ('ready' as const) : readiness.overall_status,
  };
}

export function setupSummaryHeadline(status: LscLeagueReadinessOverallStatus | undefined): string {
  switch (status) {
    case 'ready':
      return 'Ready';
    case 'blocked':
      return 'Finish setup';
    case 'warning':
      return 'Finish setup';
    case 'preview_only':
      return 'Finish setup';
    default:
      return 'Setup status';
  }
}
